import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { inject, injectable } from '@servicetitan/react-ioc';

import {
    FileUploadResultVm,
    IMobileMaintenanceServiceEntryDetailsVm,
    IMobileMaintenanceServiceEntryTypeVm,
    IMobileVehicleVm,
    MobileMaintenanceServiceEntryApi,
    MobileMaintenanceServiceEntryCreateVm,
    MobileMaintenanceServiceEntryUpdateVm,
    MobileVehicleApi,
} from '@fleet-pro/mobile-webview-api';

import { RequestManager } from '../../common/stores/request-manager';
import { isAxiosCancellationError } from '../../common/utils/is-cancellation-error';
import { RequestState } from '../../common/utils/request-states';
import { ServiceEntryFileDescriptor } from '../utils/service-entry-form-state';

@injectable()
export class ServiceEntryDataStore extends RequestManager {
    @observable serviceEntry: IMobileMaintenanceServiceEntryDetailsVm | undefined;
    @observable serviceTypes: IMobileMaintenanceServiceEntryTypeVm[] | undefined;
    @observable vehicles: IMobileVehicleVm[] | undefined;

    readonly getServiceEntryDataRequestKey = 'getServiceEntryDataRequestKey';
    readonly getServiceTypesRequestKey = 'getServiceTypesRequestKey';
    readonly getVehiclesByVinPrefixRequestKey = 'getVehiclesByVinPrefixRequestKey';
    readonly createServiceEntryRequestKey = 'createServiceEntryRequestKey';
    readonly updateServiceEntryRequestKey = 'updateServiceEntryRequestKey';
    readonly uploadFilesRequestKey = 'uploadFilesRequestKey';

    constructor(
        @inject(MobileMaintenanceServiceEntryApi)
        private readonly mobileMaintenanceServiceEntryApi: MobileMaintenanceServiceEntryApi,
        @inject(MobileVehicleApi)
        private readonly mobileVehicleApi: MobileVehicleApi
    ) {
        super();

        makeObservable(this);
    }

    @computed get fetchServiceEntryDataRequestState() {
        return this.getRequestState(this.getServiceEntryDataRequestKey);
    }

    @computed get fetchServiceTypesRequestState() {
        return this.getRequestState(this.getServiceTypesRequestKey);
    }

    @computed get fetchVehiclesByVinPrefixRequestState() {
        return this.getRequestState(this.getVehiclesByVinPrefixRequestKey);
    }

    @computed get createServiceEntryRequestState() {
        return this.getRequestState(this.createServiceEntryRequestKey);
    }

    @computed get updateServiceEntryRequestState() {
        return this.getRequestState(this.updateServiceEntryRequestKey);
    }

    @computed get uploadFilesRequestState() {
        return this.getRequestState(this.uploadFilesRequestKey);
    }

    cancelGetServiceEntryData = () => {
        this.cancelRequest(this.getServiceEntryDataRequestKey);
    };

    cancelFetchServiceTypes = () => {
        this.cancelRequest(this.getServiceTypesRequestKey);
    };

    cancelFetchVehiclesByVinPrefix = () => {
        this.cancelRequest(this.getVehiclesByVinPrefixRequestKey);
    };

    cancelCreateServiceEntry = () => {
        this.cancelRequest(this.createServiceEntryRequestKey);
    };

    cancelUpdateServiceEntry = () => {
        this.cancelRequest(this.updateServiceEntryRequestKey);
    };

    cancelUploadFiles = () => {
        this.cancelRequest(this.uploadFilesRequestKey);
    };

    @action clearVehicles = () => {
        this.vehicles = undefined;
    };

    fetchServiceEntryData = async (serviceEntryId: number) => {
        this.cancelGetServiceEntryData();
        this.resetAbortController(this.getServiceEntryDataRequestKey);

        try {
            this.setRequestState(this.getServiceEntryDataRequestKey, RequestState.Loading);

            const response = await this.mobileMaintenanceServiceEntryApi.client.getServiceEntryById(
                serviceEntryId,
                {
                    signal: this.getAbortController(this.getServiceEntryDataRequestKey)?.signal,
                }
            );

            runInAction(() => {
                this.serviceEntry = response.data;
            });

            this.setRequestState(this.getServiceEntryDataRequestKey, RequestState.Success);
        } catch (error) {
            if (isAxiosCancellationError(error)) {
                this.setRequestState(this.getServiceEntryDataRequestKey, RequestState.Idle);
            } else {
                this.setRequestState(this.getServiceEntryDataRequestKey, RequestState.Error);
            }

            throw error;
        }
    };

    fetchServiceTypes = async () => {
        this.cancelFetchServiceTypes();
        this.resetAbortController(this.getServiceTypesRequestKey);

        try {
            this.setRequestState(this.getServiceTypesRequestKey, RequestState.Loading);

            const response =
                await this.mobileMaintenanceServiceEntryApi.client.getMobileServiceEntryTypes({
                    signal: this.getAbortController(this.getServiceTypesRequestKey)?.signal,
                });

            runInAction(() => {
                this.serviceTypes = response.data?.rows;
            });

            this.setRequestState(this.getServiceTypesRequestKey, RequestState.Success);
        } catch (error) {
            if (isAxiosCancellationError(error)) {
                this.setRequestState(this.getServiceTypesRequestKey, RequestState.Idle);
            } else {
                this.setRequestState(this.getServiceTypesRequestKey, RequestState.Error);
            }

            throw error;
        }
    };

    fetchVehiclesByVinPrefix = async (vinPrefix: string) => {
        this.cancelFetchVehiclesByVinPrefix();
        this.resetAbortController(this.getVehiclesByVinPrefixRequestKey);

        try {
            this.setRequestState(this.getVehiclesByVinPrefixRequestKey, RequestState.Loading);
            const response = await this.mobileVehicleApi.client.getVehiclesByVinPrefix(vinPrefix, {
                signal: this.getAbortController(this.getVehiclesByVinPrefixRequestKey)?.signal,
            });
            runInAction(() => {
                this.vehicles = response.data?.rows;
            });

            this.setRequestState(this.getVehiclesByVinPrefixRequestKey, RequestState.Success);
        } catch (error) {
            if (isAxiosCancellationError(error)) {
                this.setRequestState(this.getVehiclesByVinPrefixRequestKey, RequestState.Idle);
            } else {
                this.setRequestState(this.getVehiclesByVinPrefixRequestKey, RequestState.Error);
            }

            throw error;
        }
    };

    createServiceEntry = async (
        serviceEntry: MobileMaintenanceServiceEntryCreateVm
    ): Promise<void> => {
        this.cancelCreateServiceEntry();
        this.resetAbortController(this.createServiceEntryRequestKey);

        try {
            this.setRequestState(this.createServiceEntryRequestKey, RequestState.Loading);

            await this.mobileMaintenanceServiceEntryApi.client.createServiceEntry(serviceEntry, {
                signal: this.getAbortController(this.createServiceEntryRequestKey)?.signal,
            });

            this.setRequestState(this.createServiceEntryRequestKey, RequestState.Success);
        } catch (error) {
            if (isAxiosCancellationError(error)) {
                this.setRequestState(this.createServiceEntryRequestKey, RequestState.Idle);
            } else {
                this.setRequestState(this.createServiceEntryRequestKey, RequestState.Error);
            }

            throw error;
        }
    };

    updateServiceEntry = async (
        serviceEntry: MobileMaintenanceServiceEntryUpdateVm
    ): Promise<void> => {
        this.cancelUpdateServiceEntry();
        this.resetAbortController(this.updateServiceEntryRequestKey);

        try {
            this.setRequestState(this.updateServiceEntryRequestKey, RequestState.Loading);

            await this.mobileMaintenanceServiceEntryApi.client.updateServiceEntry(serviceEntry, {
                signal: this.getAbortController(this.updateServiceEntryRequestKey)?.signal,
            });

            this.setRequestState(this.updateServiceEntryRequestKey, RequestState.Success);
        } catch (error) {
            if (isAxiosCancellationError(error)) {
                this.setRequestState(this.updateServiceEntryRequestKey, RequestState.Idle);
            } else {
                this.setRequestState(this.updateServiceEntryRequestKey, RequestState.Error);
            }

            throw error;
        }
    };

    uploadFiles = async (files: ServiceEntryFileDescriptor[]): Promise<FileUploadResultVm[]> => {
        this.cancelUploadFiles();
        this.resetAbortController(this.uploadFilesRequestKey);

        try {
            this.setRequestState(this.uploadFilesRequestKey, RequestState.Loading);

            const formData = new FormData();

            files.forEach((file, index) => {
                if (file.file instanceof File) {
                    formData.append(`${index}`, file.file);
                }
            });

            const response =
                await this.mobileMaintenanceServiceEntryApi.client.uploadFileAttachments({
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' },
                    signal: this.getAbortController(this.uploadFilesRequestKey)?.signal,
                });

            const data = response.data?.rows ?? [];

            this.setRequestState(this.uploadFilesRequestKey, RequestState.Success);

            return data;
        } catch (error) {
            if (isAxiosCancellationError(error)) {
                this.setRequestState(this.uploadFilesRequestKey, RequestState.Idle);
            } else {
                this.setRequestState(this.uploadFilesRequestKey, RequestState.Error);
            }

            throw error;
        }
    };

    /* downloadFileAttachment = (attachmentId: number, baseUrl: string) => {
        const downloadPath = MobileMaintenanceServiceEntryClient.downloadFileAttachmentPath.replace(
            '{attachmentId}',
            attachmentId.toString()
        );

        return new URL(`${baseUrl}${downloadPath}`).toString();
    };*/
}
